import React from 'react';
import {
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { RowActionsMenu, RowAction } from './ui/RowActionsMenu';
import {
  useUpdateQuoteStatus,
  useDeleteQuote,
  useConvertQuoteToJob,
  useSendQuote,
} from '../hooks/useQuotesData';
import { quotesService } from '../services/quotesService';
import { useToast } from '../contexts/ToastContext';

interface QuoteActionsProps {
  quote: any;
  onEdit?: (quote: any) => void;
  onView?: (quote: any) => void;
}

export default function QuoteActions({ quote, onEdit, onView }: QuoteActionsProps) {
  const updateStatusMutation = useUpdateQuoteStatus();
  const deleteQuoteMutation = useDeleteQuote();
  const convertToJobMutation = useConvertQuoteToJob();
  const sendQuoteMutation = useSendQuote();
  const { showToast } = useToast();

  const status = String(quote?.status || '').toUpperCase();

  const handleDownloadPdf = async () => {
    try {
      const blob = await quotesService.getQuotePDF(quote.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quote-${quote.quoteNumber || quote.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to download PDF', 'error');
    }
  };

  const handleCopyLink = async () => {
    const token = quote?.publicToken;
    if (!token) {
      showToast('Send the quote first to generate a shareable link.', 'info');
      return;
    }
    const url = `${window.location.origin}/q/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Public link copied to clipboard', 'success');
    } catch {
      showToast(url, 'info');
    }
  };

  const handleDuplicate = async () => {
    try {
      await quotesService.createQuote({
        title: `${quote.title || 'Quote'} (copy)`,
        description: quote.description || '',
        clientId: quote.clientId,
        siteId: quote.siteId || undefined,
        address: quote.address || '',
        postcode: quote.postcode || '',
        priority: quote.priority || 'MEDIUM',
        estimatedCost: quote.estimatedCost,
        estimatedRevenue: quote.estimatedRevenue,
        estimatedDuration: quote.estimatedDuration,
        notes: quote.notes || undefined,
      } as any);
      showToast('Quote duplicated as a new draft', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to duplicate quote', 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!window.confirm('Delete this quote? This cannot be undone.')) return;
    try {
      await deleteQuoteMutation.mutateAsync(quote.id);
      showToast('Quote deleted', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to delete quote', 'error');
    }
  };

  const actions: RowAction[] = [
    {
      id: 'view',
      label: 'View details',
      icon: EyeIcon,
      onClick: () => onView?.(quote),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: PencilIcon,
      onClick: () => onEdit?.(quote),
      hidden: status === 'ACCEPTED' || status === 'REJECTED',
    },
    {
      id: 'send',
      label: 'Send to client',
      icon: PaperAirplaneIcon,
      description: 'Email the quote and track when the client opens it.',
      hidden: status !== 'DRAFT',
      onClick: async () => {
        try {
          await sendQuoteMutation.mutateAsync(quote.id);
          showToast('Quote sent', 'success');
        } catch (err: any) {
          showToast(err?.response?.data?.message || 'Failed to send quote', 'error');
        }
      },
    },
    {
      id: 'accept',
      label: 'Mark as accepted',
      icon: CheckCircleIcon,
      hidden: status !== 'SENT' && status !== 'CHANGES_REQUESTED',
      onClick: async () => {
        try {
          await updateStatusMutation.mutateAsync({
            id: quote.id,
            status: 'ACCEPTED' as any,
            notes: 'Marked accepted by admin',
          });
          showToast('Quote accepted', 'success');
        } catch (err: any) {
          showToast(err?.response?.data?.message || 'Failed to update', 'error');
        }
      },
    },
    {
      id: 'reject',
      label: 'Mark as rejected',
      icon: XCircleIcon,
      hidden: status !== 'SENT' && status !== 'CHANGES_REQUESTED',
      onClick: async () => {
        try {
          await updateStatusMutation.mutateAsync({
            id: quote.id,
            status: 'REJECTED' as any,
            notes: 'Marked rejected by admin',
          });
          showToast('Quote rejected', 'success');
        } catch (err: any) {
          showToast(err?.response?.data?.message || 'Failed to update', 'error');
        }
      },
    },
    {
      id: 'convert',
      label: 'Convert to job',
      icon: ArrowRightIcon,
      description: 'Create a scheduled job from this quote.',
      hidden: status !== 'ACCEPTED',
      onClick: async () => {
        try {
          await convertToJobMutation.mutateAsync(quote.id);
          showToast('Job created from quote', 'success');
        } catch (err: any) {
          showToast(err?.response?.data?.message || 'Failed to convert', 'error');
        }
      },
    },
    {
      id: 'download',
      label: 'Download PDF',
      icon: ArrowDownTrayIcon,
      divider: true,
      onClick: handleDownloadPdf,
    },
    {
      id: 'copy-link',
      label: 'Copy public link',
      icon: LinkIcon,
      onClick: handleCopyLink,
      disabled: !quote?.publicToken,
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: DocumentDuplicateIcon,
      onClick: handleDuplicate,
    },
    {
      id: 'delete',
      label: 'Delete quote',
      icon: TrashIcon,
      danger: true,
      divider: true,
      onClick: handleConfirmDelete,
    },
  ];

  return <RowActionsMenu actions={actions} label="Quote actions" />;
}
